/** Nombre maximum de caractères pour un message de chat (après trim côté API). */
export const CHAT_MESSAGE_MAX_LENGTH = 500;

/** Sujet d’un fil de discussion client ↔ praticien (après trim côté API). */
export const CHAT_THREAD_SUBJECT_MIN_LENGTH = 3;
export const CHAT_THREAD_SUBJECT_MAX_LENGTH = 200;

export type Plan = "FREE" | "PRO" | "BUSINESS";
export type Role = "ADMIN" | "AGENT";
import type { ClientPortalFeatures } from "@cliently/schemas";
export type { ClientPortalFeatures };
import type { ProfessionType } from "./professions";
export type { ProfessionType } from "./professions";
export {
  PROFESSION_TYPES,
  professionSelectLabelKey,
  settingsProfessionTranslationKey,
  portalProfessionTranslationKey,
} from "./professions";
export {
  resolveNumericTrackingConfigured,
  resolveEffectiveAssignedMetricIds,
} from "./client-portal-features";
export type ClientStatus = "ACTIVE" | "INACTIVE" | "PAUSED";
export type AppointmentStatus = "PENDING" | "CONFIRMED" | "DONE" | "CANCELLED";

/** En PENDING : qui doit valider le créneau. */
export type PendingConfirmationBy = "CLIENT" | "PRACTITIONER";
export type InvoiceStatus =
  | "DRAFT"
  | "SENT"
  | "PAID"
  | "REFUNDED"
  | "CANCELLED"
  | "CREDIT_ISSUED";

export type InvoiceDocumentType = "INVOICE" | "CREDIT_NOTE" | "QUOTE";
export type ClientType = "INDIVIDUAL" | "COMPANY";
export type InvitationStatus = "PENDING" | "ACCEPTED" | "CANCELLED" | "EXPIRED";
export type OverrideType = "DAY_OFF" | "CUSTOM_HOURS";

export type ClientRequestType = "DEMANDE" | "RECLAMATION";
export type ClientRequestStatus = "OPEN" | "IN_PROGRESS" | "RESOLVED";

export type IntakeFieldType =
  | "text"
  | "email"
  | "phone"
  | "select"
  | "textarea"
  | "date"
  | "number"
  | "document";

/** Type de représentation graphique pour une statistique de champ formulaire. */
export type IntakeStatChartType =
  | "pie"
  | "donut"
  | "bar"
  | "line"
  | "histogram";

/** Granularité temporelle pour les champs de type `date`. */
export type IntakeStatTimeBucket = "month" | "quarter" | "year";

/**
 * Mode d'agrégation pour un champ `date`.
 * - `occurrence` (défaut) : on regroupe les dates par mois/trimestre/année.
 * - `age` : on calcule l'âge (années révolues entre la date saisie et "maintenant")
 *   et on agrège ce dérivé sur des `bins`. Permet d'avoir une stat d'âge qui évolue
 *   organiquement avec le temps (sans demander un chiffre à chaque client).
 */
export type IntakeStatDateMode = "occurrence" | "age";

/** Tranche pour binning d'un champ numérique (ex. âge). `min` inclus, `max` exclu. */
export interface IntakeStatBin {
  min?: number;
  max?: number;
  label: string;
}

export interface IntakeFieldStatistics {
  enabled: boolean;
  chartType?: IntakeStatChartType;
  /** Pour les champs `number` ou `date` en mode `age`. */
  bins?: IntakeStatBin[];
  /** Pour les champs `date` en mode `occurrence`. */
  timeBucket?: IntakeStatTimeBucket;
  /** Pour les champs `date`. Si absent, `occurrence` par défaut. */
  dateMode?: IntakeStatDateMode;
}

export interface IntakeField {
  id: string;
  label: string;
  type: IntakeFieldType;
  required: boolean;
  options?: string[];
  /** Configuration optionnelle pour exposer des stats sur le dashboard. */
  statistics?: IntakeFieldStatistics;
}

export type IntakeForm = IntakeField[];

export interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: Plan;
  professionType: ProfessionType;
  customProfession: string | null;
  stripeAccountId: string | null;
  stripeCustomerId: string | null;
  logo: string | null;
  siret: string | null;
  rpps: string | null;
  rcpMediatorInfo: string | null;
  tva: string | null;
  address: string | null;
  /** Téléphone public du cabinet (pied de page de la vitrine). */
  contactPhone?: string | null;
  /** Email public du cabinet (pied de page de la vitrine). */
  contactEmail?: string | null;
  instagram: string | null;
  website: string | null;
  profilePhoto: string | null;
  /** Recadrage de la photo de profil (valeur CSS object-position). */
  profilePhotoPosition?: string | null;
  bio: string | null;
  /** Galerie « À propos » : URL + légende optionnelle (4 max). */
  aboutImages?: Array<{ url: string; caption?: string | null }> | null;
  diplomas: Array<{ label: string; imageUrl?: string | null }> | null;
  /** Bannière de couverture de la page de marque. */
  coverImage?: string | null;
  /** Active la page de marque publique (/p/:slug). */
  storefrontEnabled?: boolean;
  /** Accroche affichée sous le nom sur la vitrine. */
  storefrontTagline?: string | null;
  /** Phrase d'appel à l'action en fin de page de marque. */
  storefrontCtaText?: string | null;
  /** Fond de la cellule d'accroche de fin de page (hex). */
  storefrontCtaBgColor?: string | null;
  /** Couleur du texte de l'accroche de fin de page (hex). */
  storefrontCtaTextColor?: string | null;
  /** Image optionnelle de l'accroche de fin de page. */
  storefrontCtaImage?: string | null;
  /** Couleur du texte de la biographie sur la vitrine (hex). */
  storefrontBioTextColor?: string | null;
  /** Taille du texte de la biographie (`sm`, `base`, `lg`). */
  storefrontBioFontSize?: "sm" | "base" | "lg" | null;
  /** Avantages éditables affichés sur la vitrine. */
  storefrontAdvantages?: Array<{
    title: string;
    text?: string;
    icon?: string;
    bgColor?: string;
    iconBgColor?: string;
    iconColor?: string;
    textColor?: string;
  }> | null;
  /** Palette de couleurs de la page de marque (`bleu` = défaut). */
  storefrontTheme?: string | null;
  /** Couleur de fond personnalisée de la vitrine (hex). */
  storefrontBackgroundColor?: string | null;
  /** Position de la photo de profil dans l'en-tête de la vitrine (`LEFT` = défaut). */
  storefrontProfileLayout?: "LEFT" | "CENTER" | "RIGHT" | null;
  /** Couleur d'accent personnalisée de la vitrine (hex), prime sur les 8 thèmes prédéfinis. */
  storefrontAccentColor?: string | null;
  /** Options portail (métriques, etc.). */
  clientPortalFeatures?: ClientPortalFeatures | null;
  /** Autorise (ou non) les clients à laisser une note sur chaque séance d'un programme. */
  clientProgramSessionNotesEnabled?: boolean;
  /** Cabinet exerçant une profession de santé (avertissement dictée IA). */
  isHealthProfessional?: boolean;
  /** Dictées vocales avec traitement IA Mistral. */
  voiceReportsEnabled?: boolean;
  accessCode: string | null;
  bufferMinutes: number;
  reminderHour: number;
  timezone?: string;
  registrationMinLeadMinutes?: number;
  registrationMaxAdvanceMinutes?: number;
  depositEnabled: boolean;
  defaultSessionPrice: number;
  /** Minutes — réservation sans prestation catalogue, défaut agenda. */
  defaultSessionDuration: number;
  depositPercent: number;
  nonRefundableDeposit: boolean;
  onboardingCompleted: boolean;
  /** Si true, les agents ne voient que leurs propres clients/RDV/factures. */
  agentScopingEnabled: boolean;
  intakeForm: IntakeForm | null;
  intakeFormVersion: number;
  invoiceNumberTemplate: string | null;
  invoiceNumberSeqWidth: number;
  invoiceNumberSeqStart: number;
  creditNoteNumberTemplate: string | null;
  creditNoteSeqWidth: number;
  creditNoteSeqStart: number;
  /** Politique d’annulation / modification affichée sur le portail client. */
  clientCancellationPolicyText?: string | null;
  /** Délai minimum (minutes) avant le RDV pour annuler ou reprogrammer (0 = aucune restriction). */
  clientCancellationMinLeadMinutes?: number;
  createdAt: Date;
}

export interface User {
  id: string;
  clerkId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: Role;
  orgId: string;
  hasVisioAccess: boolean;
}

export interface ClientMetaData {
  shared: Record<string, unknown>;
  private: Record<string, unknown>;
}

export interface Client {
  id: string;
  orgId: string;
  assignedToId: string | null;
  clientType: ClientType;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  photo: string | null;
  specialty: string | null;
  companyName: string | null;
  companySiret: string | null;
  tags: string[];
  status: ClientStatus;
  metaData: ClientMetaData | null;
  intakeData: Record<string, unknown> | null;
  intakeFormVersion: number;
  intakeRequiresUpdate: boolean;
  hasAccount: boolean;
  userId: string | null;
  portalToken: string | null;
  lastLoginAt: Date | null;
  invitedAt: Date | null;
  accountCreatedAt: Date | null;
  stripeCustomerId: string | null;
  createdAt: Date;
}

export interface PortalClientData {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  photo: string | null;
  sharedMeta: Record<string, unknown>;
  appointments: Pick<
    Appointment,
    "id" | "startsAt" | "duration" | "status" | "type"
  >[];
  invoices: Pick<
    Invoice,
    "id" | "number" | "amount" | "status" | "dueAt" | "paidAt" | "pdfUrl"
  >[];
  organization: Pick<
    Organization,
    "name" | "logo" | "professionType" | "customProfession"
  >;
}

export interface ServiceLocation {
  id: string;
  orgId: string;
  name: string;
  address: string | null;
  details: string | null;
  createdAt: Date;
}

export interface ServiceItemLocation {
  serviceItemId: string;
  locationId: string;
}

export interface ServiceItem {
  id: string;
  orgId: string;
  label: string;
  duration: number;
  price: number;
  maxBookingsPerClient?: number | null;
  description?: Record<string, unknown> | null;
  image?: string | null;
  /** Cadrage vertical de la vignette (`object-cover` + object-position). */
  imageObjectPosition?: "TOP" | "CENTER" | "BOTTOM";
  /** Couleur hexadécimale (#RRGGBB) du motif, utilisée pour colorer les rendez-vous dans l'agenda. */
  color?: string | null;
  isActive: boolean;
  /** Visible sur la page de marque publique (/p/:slug). */
  publicListed?: boolean;
  position: number;
  /** Groupe d'agencement sur la vitrine (null = "sans groupe"). */
  groupId?: string | null;
  registrationMinLeadMinutes?: number;
  registrationMaxAdvanceMinutes?: number;
  locationMode?: "ONSITE" | "REMOTE";
  /** Consignes affichées au client avant la séance. */
  clientPreparationNotes?: string | null;
  /** Nombre de pratiquants acceptés simultanément sur un même créneau (1 = individuel). */
  maxParticipants?: number;
  /** Créneaux jour/horaire dédiés (vide = utilise les disponibilités générales du praticien). */
  schedules?: ServiceSchedule[];
  createdAt: Date;
}

/** Créneau récurrent (jour + horaire) dédié à une prestation collective. */
export interface ServiceSchedule {
  id: string;
  serviceItemId: string;
  dayOfWeek: number; // 0=Sunday ... 6=Saturday
  startTime: string; // "18:00"
  endTime: string; // "19:00"
  isActive: boolean;
}

export type WaitlistStatus = "WAITING" | "PROMOTED" | "CANCELLED";

/** Entrée de liste d'attente pour un créneau de prestation collective complet. */
export interface ServiceWaitlistEntry {
  id: string;
  orgId: string;
  serviceItemId: string;
  clientId: string;
  client?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string | null;
  };
  startsAt: Date;
  status: WaitlistStatus;
  position?: number;
  createdAt: Date;
}

/** Avertissement pro (absence...) cumulable, jamais visible côté client. */
export interface ClientWarning {
  id: string;
  orgId: string;
  clientId: string;
  appointmentId: string | null;
  reason: string | null;
  createdById: string | null;
  createdAt: Date;
}

/** Places disponibles sur un créneau (null = prestation individuelle, pas de compteur affiché). */
export interface SlotCapacity {
  max: number;
  booked: number;
  remaining: number;
  isFull: boolean;
  waitlistCount: number;
}

/** Groupe d'agencement des services sur la vitrine publique. */
export interface ServiceItemGroup {
  id: string;
  orgId: string;
  label: string;
  position: number;
  createdAt: Date;
}

export interface Appointment {
  id: string;
  orgId: string;
  clientId: string;
  assignedToId: string | null;
  serviceItemId: string | null;
  locationId: string | null;
  location?: { id: string; name: string; address?: string | null } | null;
  resourceId: string | null;
  resource?: { id: string; label: string } | null;
  startsAt: Date;
  duration: number;
  price: number;
  status: AppointmentStatus;
  pendingConfirmationBy?: PendingConfirmationBy;
  /** Renseigné lorsque le RDV est passé en confirmé. */
  confirmedAt?: Date | null;
  type: string | null;
  invoiceId: string | null;
  notes: string | null;
  practitionerNote: string | null;
  dailyRoomUrl: string | null;
  dailyRoomName: string | null;
  isRecurring: boolean;
  recurrence: string | null;
  depositAmount: number;
  depositPaid: boolean;
  stripeDepositSession: string | null;
  createdAt: Date;
  /** Nombre d'avertissements cumulés du client (absences...), visible pro uniquement. */
  clientWarningsCount?: number;
  /** Renseigné pour les RDV liés à une prestation collective (maxParticipants > 1). */
  groupSession?: {
    maxParticipants: number;
    participants: Array<{
      id: string;
      clientId: string;
      status: AppointmentStatus;
      client: {
        id: string;
        firstName: string;
        lastName: string;
        email?: string | null;
        photo?: string | null;
      };
    }>;
    waitlist: ServiceWaitlistEntry[];
  };
}

export interface Note {
  id: string;
  clientId: string;
  content: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
}

export interface Objective {
  id: string;
  clientId: string;
  title: string;
  description: string | null;
  progress: number;
  clientProgressEditable: boolean;
  doneAt: Date | null;
  recurrenceType?: "DAILY" | "WEEKLY" | "MONTHLY";
  startDate?: Date;
  endDate?: Date;
  icon?: string | null;
  target?:
    | { type: "NUMBER"; value: number; unit?: string }
    | { type: "DURATION"; value: number; unit?: "seconds" }
    | { type: "BOOLEAN"; value: boolean; unit?: string }
    | { type: "AMOUNT"; value: number; unit?: string }
    | { type: "FREQUENCY"; value: number; unit?: string }
    | null;
  days?: Array<{
    id: string;
    objectiveId?: string;
    date: string; // YYYY-MM-DD
    progress: number;
    doneAt: Date | null;
  }>;
  createdAt: Date;
}

export interface Document {
  id: string;
  clientId: string;
  name: string;
  /** Libellé affiché pour le client ; si absent, utiliser `name`. */
  label?: string | null;
  url: string;
  type: string;
  createdAt: Date;
}

export interface ClientRequest {
  id: string;
  orgId: string;
  clientId: string;
  type: ClientRequestType;
  subject: string;
  message: string;
  status: ClientRequestStatus;
  responseMessage: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface Invoice {
  id: string;
  orgId: string;
  clientId: string;
  createdById: string | null;
  number: string;
  amount: number;
  vatRate: number;
  vatExempt: boolean;
  status: InvoiceStatus;
  documentType: InvoiceDocumentType;
  creditedInvoiceId: string | null;
  stripePaymentId: string | null;
  pdfUrl: string | null;
  dueAt: Date;
  paidAt: Date | null;
  appointmentId: string | null;
  introTitle: string | null;
  introText: string | null;
  description: string | null;
  deliveryAddress: string | null;
  createdAt: Date;
}

export interface Notification {
  id: string;
  orgId: string;
  userId: string | null;
  type: string;
  title: string;
  message: string;
  read: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: Date;
}

export interface AdminLog {
  id: string;
  adminId: string;
  action: string;
  targetId: string;
  metadata: Record<string, unknown> | null;
  createdAt: Date;
}

export interface AuditLog {
  id: string;
  orgId: string;
  userId: string | null;
  action: string;
  entity: string;
  entityId: string | null;
  changes: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: Date;
}

export interface PushSubscription {
  id: string;
  orgId: string;
  userId: string;
  endpoint: string;
  p256dh: string;
  auth: string;
  createdAt: Date;
}

export type AuditAction =
  | "CREATE"
  | "UPDATE"
  | "DELETE"
  | "EXPORT"
  | "ANONYMIZE"
  | "RECTIFY"
  | "IMPORT"
  | "INVITE"
  | "CANCEL"
  | "CONFIRM"
  | "SEND"
  | "PAY";

export type AuditEntity =
  | "CLIENT"
  | "APPOINTMENT"
  | "INVOICE"
  | "ORGANIZATION"
  | "MEMBER"
  | "AVAILABILITY"
  | "NOTE"
  | "OBJECTIVE"
  | "DOCUMENT"
  | "RGPD";

export const PERMISSIONS = {
  CLIENTS_VIEW: "clients:view",
  CLIENTS_CREATE: "clients:create",
  CLIENTS_EDIT: "clients:edit",
  CLIENTS_DELETE: "clients:delete",
  APPOINTMENTS_VIEW: "appointments:view",
  APPOINTMENTS_CREATE: "appointments:create",
  APPOINTMENTS_EDIT: "appointments:edit",
  INVOICES_VIEW: "invoices:view",
  INVOICES_CREATE: "invoices:create",
  PROGRAMS_VIEW: "programs:view",
  PROGRAMS_EDIT: "programs:edit",
  EXERCISES_VIEW: "exercises:view",
  EXERCISES_EDIT: "exercises:edit",
  RESOURCES_VIEW: "resources:view",
  RESOURCES_EDIT: "resources:edit",
  PACKAGES_VIEW: "packages:view",
  PACKAGES_EDIT: "packages:edit",
  PROMOTIONS_VIEW: "promotions:view",
  PROMOTIONS_EDIT: "promotions:edit",
  SETTINGS_VIEW: "settings:view",
  SETTINGS_EDIT: "settings:edit",
  MEMBERS_MANAGE: "members:manage",
  AUDIT_VIEW: "audit:view",
  FORMS_VIEW: "forms:view",
  FORMS_EDIT: "forms:edit",
  FORMS_SEND: "forms:send",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  ADMIN: Object.values(PERMISSIONS),
  AGENT: [
    PERMISSIONS.CLIENTS_VIEW,
    PERMISSIONS.CLIENTS_CREATE,
    PERMISSIONS.CLIENTS_EDIT,
    PERMISSIONS.CLIENTS_DELETE,
    PERMISSIONS.APPOINTMENTS_VIEW,
    PERMISSIONS.APPOINTMENTS_CREATE,
    PERMISSIONS.APPOINTMENTS_EDIT,
    PERMISSIONS.INVOICES_VIEW,
    PERMISSIONS.INVOICES_CREATE,
    PERMISSIONS.PROGRAMS_VIEW,
    PERMISSIONS.PROGRAMS_EDIT,
    PERMISSIONS.EXERCISES_VIEW,
    PERMISSIONS.EXERCISES_EDIT,
    PERMISSIONS.RESOURCES_VIEW,
    PERMISSIONS.PACKAGES_VIEW,
    PERMISSIONS.PACKAGES_EDIT,
    PERMISSIONS.PROMOTIONS_VIEW,
    PERMISSIONS.PROMOTIONS_EDIT,
    PERMISSIONS.SETTINGS_VIEW,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.FORMS_VIEW,
    PERMISSIONS.FORMS_EDIT,
    PERMISSIONS.FORMS_SEND,
  ],
};

export interface Invitation {
  id: string;
  orgId: string;
  email: string;
  role: Role;
  token: string;
  status: InvitationStatus;
  invitedBy: string;
  expiresAt: Date;
  createdAt: Date;
}

export interface AvailabilityOverride {
  id: string;
  orgId: string;
  userId: string | null;
  startDate: Date;
  endDate: Date;
  type: OverrideType;
  startTime: string | null;
  endTime: string | null;
  reason: string | null;
  createdAt: Date;
}

export const PLAN_LIMITS = {
  FREE: { clients: 5, appointments: 20, payments: false, members: 0 },
  PRO: {
    clients: Infinity,
    appointments: Infinity,
    payments: true,
    members: 5,
  },
  BUSINESS: {
    clients: Infinity,
    appointments: Infinity,
    payments: true,
    members: Infinity,
  },
} as const;

export type PlanLimits = (typeof PLAN_LIMITS)[Plan];

export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type PackageUnitType = "HOURS" | "SESSIONS" | "UNITS";

export interface Resource {
  id: string;
  orgId: string;
  label: string;
  description: string | null;
  isActive: boolean;
  createdAt: Date;
}

export interface Package {
  id: string;
  orgId: string;
  clientId: string;
  serviceItemId: string | null;
  label: string;
  unitType: PackageUnitType;
  totalUnits: number;
  usedUnits: number;
  expiresAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface ServiceItemAssignment {
  id: string;
  serviceItemId: string;
  userId: string;
}

export interface ClientOrgMembership {
  clientId: string;
  orgId: string;
  orgName: string;
  orgLogo: string | null;
  orgProfilePhoto: string | null;
  joinedAt: string;
  /** Durée par défaut (minutes) pour réserver sans prestation au catalogue. */
  defaultSessionDuration: number;
  /** Métier du praticien. */
  professionType: ProfessionType;
  /** Options portail — même objet que Organization.clientPortalFeatures. */
  clientPortalFeatures?: ClientPortalFeatures | null;
}
